import React, { useState } from 'react';
import {
  Table, Button, Modal, Form, FormItem, Input, TextArea, Card, useToast, showConfirm
} from '../ui';
import type { Column } from '../ui';
import {
  PlusIcon, PencilIcon, TrashIcon, TagIcon, MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { Brand } from '../config/supabase';
import { useBrands, useCreateBrand, useUpdateBrand, useDeleteBrand } from '../hooks/api/useBrands';

interface BrandFormData {
  name: string;
  description?: string;
}

const BrandManagement: React.FC = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  
  const { success, error: showError } = useToast();

  // React Query hooks
  const { 
    data: brandsData, 
    isLoading: loading, 
    error: brandsError 
  } = useBrands(currentPage, pageSize, searchTerm);

  const createBrandMutation = useCreateBrand();
  const updateBrandMutation = useUpdateBrand();
  const deleteBrandMutation = useDeleteBrand();

  // Extract data from hooks
  const brands = brandsData?.brands || [];
  const totalCount = brandsData?.totalCount || 0;

  // Handle mutation errors
  React.useEffect(() => {
    if (brandsError) {
      showError('Error fetching brands: ' + brandsError.message);
    }
  }, [brandsError, showError]);

  // Handle search with debouncing
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when searching
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1); // Reset to first page when changing page size
    }
  };

  const handleAdd = () => {
    setEditingBrand(null);
    setModalVisible(true);
  };

  const handleEdit = (brand: Brand) => {
    setEditingBrand(brand);
    setModalVisible(true);
  };

  const handleDelete = async (id: string, brandName: string) => {
    const confirmed = await showConfirm({
      title: `Delete brand "${brandName}"?`,
      content: 'This action cannot be undone.',
      onConfirm: () => {},
      type: 'danger'
    });
    
    if (!confirmed) return;

    try {
      await deleteBrandMutation.mutateAsync(id);
      success('Brand deleted successfully');
    } catch (error: any) {
      showError('Error deleting brand: ' + error.message);
    }
  };

  const handleSubmit = async (values: BrandFormData) => {
    try {
      if (editingBrand) {
        await updateBrandMutation.mutateAsync({
          id: editingBrand.id,
          ...values
        });
        success('Brand updated successfully');
      } else {
        await createBrandMutation.mutateAsync(values);
        success('Brand created successfully');
      }

      handleModalClose();
    } catch (error: any) {
      showError('Error saving brand: ' + error.message);
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingBrand(null);
  };

  const columns: Column<Brand>[] = [
    {
      key: 'name',
      title: 'Name',
      dataIndex: 'name',
      sorter: (a: Brand, b: Brand) => a.name.localeCompare(b.name),
      render: (name: string) => <span className="font-semibold text-gray-900">{name}</span>,
    },
    {
      key: 'description',
      title: 'Description',
      dataIndex: 'description',
      render: (description: string) => (
        <span className="text-gray-600">
          {description || 'No description'}
        </span>
      ),
    },
    {
      key: 'created_at',
      title: 'Created',
      dataIndex: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 120,
      render: (_, record: Brand) => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(record)}
            className="text-blue-600 hover:text-blue-800"
          >
            <PencilIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              showConfirm({
                title: 'Delete Brand',
                content: `Are you sure you want to delete "${record.name}"? This action cannot be undone.`,
                onConfirm: () => handleDelete(record.id, record.name),
                confirmText: 'Delete',
                type: 'danger'
              });
            }}
            className="text-red-600 hover:text-red-800"
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <TagIcon className="h-6 w-6 mr-2" />
          Brand Management
        </h1>
        <p className="text-gray-600 mt-1">
          Manage dress brands and their information
        </p>
      </div>

      <div className="flex justify-between items-center">
        <Button
          variant="primary"
          size="lg"
          onClick={handleAdd}
          className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-semibold px-6 py-3"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add New Brand
        </Button>
        
        <div className="ml-auto">
          <Input
            placeholder="Search brands by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<MagnifyingGlassIcon className="h-4 w-4" />}
            className="w-80"
          />
        </div>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={brands}
          loading={loading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange
          }}
        />
      </Card>

      <Modal
        isOpen={modalVisible}
        onClose={handleModalClose}
        title={editingBrand ? 'Edit Brand' : 'Add New Brand'}
        size="md"
      >
        <Form
          onSubmit={handleSubmit}
          initialValues={editingBrand || {}}
        >
          <div className="space-y-4">
            <FormItem name="name" label="Brand Name" required>
              <Input
                placeholder="Enter brand name"
                autoFocus
                maxLength={100}
              />
            </FormItem>

            <FormItem name="description" label="Description">
              <TextArea
                placeholder="Enter brand description..."
                rows={4}
                maxLength={500}
              />
            </FormItem>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <Button variant="outline" onClick={handleModalClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {editingBrand ? 'Update Brand' : 'Add Brand'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default BrandManagement; 